import { useCallback, useContext, useEffect, useState } from "react"
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronCircleDown } from "@fortawesome/pro-solid-svg-icons"
import { faAngleDoubleLeft, faAngleDoubleRight } from "@fortawesome/free-solid-svg-icons"

import { ApplicationContext } from 'context'

import Uploader from "components/Uploader"
import ButtonPrimary from "components/ButtonPrimary"

import { discectError } from "lib/utils"
import api from 'lib/api'

import styles from "./styles.module.scss"

const OnBoardingUploads = ({ surety, agency, token, requirements, onRefresh }) => {
  const { contextError, contextLoading } = useContext(ApplicationContext)
  const [isLoading, setIsLoading] = contextLoading // eslint-disable-line
  const [_, setError] = contextError // eslint-disable-line
  const [files, setFiles] = useState(null)
  const [displayStates, setDisplayStates] = useState(null)

  const navigate = useNavigate()

  const { uploads: requiredUploads } = requirements

  useEffect(() => {
    if (requiredUploads.length && !displayStates) {
      let states = {}
      requiredUploads.forEach(itm => {
        states[itm.id] = { isCompleted: false, isDisplaying: true }
      })
      setDisplayStates(states)
    }
  }, [displayStates, requiredUploads])

  const _fetchSecureUrls = useCallback(async () => {
    setIsLoading(true)

    try {
      const { files: incomingFiles } = await api.files.fetchSecureUrls(token, {})

      let currentDisplayStates = { ...displayStates }
      requiredUploads.forEach(u => {
        if (u.isDoubleSided) {
          let fileType = u.name.replace(/ /g, '_').toUpperCase()
          const frontFile = incomingFiles.find(itm => (itm.metadata.uploadid === u.id && itm.metadata.type === `${fileType}_FRONT`))
          const backFile = incomingFiles.find(itm => (itm.metadata.uploadid === u.id && itm.metadata.type === `${fileType}_BACK`))
          if (frontFile && backFile) {
            currentDisplayStates[u.id].isCompleted = true
            currentDisplayStates[u.id].isDisplaying = false
          }
        }
      })

      setDisplayStates(currentDisplayStates)
      setFiles(incomingFiles)
    } catch(e) {
      setError(discectError(e))
    }
    setIsLoading(false)
  }, [displayStates, requiredUploads, token, setError, setIsLoading])

  useEffect(() => {
    if (displayStates && token && !files) _fetchSecureUrls()
  }, [displayStates, token, files, _fetchSecureUrls])

  const _toggleDisplayState = id => {
    let currentDisplayStates = { ...displayStates }
    currentDisplayStates[id].isDisplaying = !currentDisplayStates[id].isDisplaying
    setDisplayStates(currentDisplayStates)
  }

  // Uploader

  const _uploadCompleted = async (results, id) => {  
    setIsLoading(true)

    try {
      if (results.length) {
        const key = results[0].params.key
        await api.files.uploadComplete(token, { key })
        await _fetchSecureUrls()
        await onRefresh(['UPLOADS'])
      } else {
        throw new Error('Results were invalid')
      }
    } catch(e) {
      setError(discectError(e))
      setIsLoading(false)
    }
  }

  const _fetchSignedUrl = params => {
    const [type, id] = params.id.split('_')

    // let sessionInfoJson = getSessionInfo()
    
    let meta = {}
    const upload = requiredUploads.find(itm => itm.id === id)
    if (upload) {
      let name = upload.name
      let fileType = upload.name.replace(/ /g, '_').toUpperCase()
      let description = `Upload of the users ${upload.name}`
      if (upload.isDoubleSided) {
        if (type.toUpperCase() === 'FRONT') {
          name = `${upload.name} - Front`
          description = `Front of the ${upload.name}`
          fileType = `${fileType}_FRONT`
        } else {
          name = `${upload.name} - Back`
          description = `Back of the ${upload.name}`
          fileType = `${fileType}_BACK`
        }
      }
      meta = {
        uploadid: upload.id,
        name,
        description,
        type: fileType,
        usertype: 'OWNER'
      }
    }
    
    return api.files.fetchSignedUrl(token, { ...params, meta })
  }

  const _getRequiredUploads = () => {
    return requiredUploads
      .map(u => {
        if (u.isDoubleSided) {
          let fileType = u.name.replace(/ /g, '_').toUpperCase()
          const displayState = displayStates[u.id]
          const frontFile = files.find(itm => (itm.metadata.uploadid === u.id && itm.metadata.type === `${fileType}_FRONT`))
          const backFile = files.find(itm => (itm.metadata.uploadid === u.id && itm.metadata.type === `${fileType}_BACK`))

          let classes = styles.uploads__upload__container
          if (displayState.isDisplaying) classes += ` ${styles.uploads__upload__container__on}`
          return (
            <div className={classes}>
              <header className={styles.uploads__header}>
                <div className={styles.uploads__header__left}>
                  <h3>{u.name}</h3>
                  <p>This is something</p>
                </div>
                <div className={styles.uploads__header__right}>
                  {(frontFile && backFile) && <div className={styles.bigTile}>Completed!</div>}
                  <button onClick={() => _toggleDisplayState(u.id)}><FontAwesomeIcon icon={faChevronCircleDown} /></button>
                </div>
              </header>
              {displayState.isDisplaying && (
                <div className={styles.uploads__upload__panels}>
                  <Uploader
                    id={`front_${u.id}`}
                    key={`front_${u.id}`}
                    content={(
                      <div className={styles.uploads__upload}>
                        {frontFile && (
                          <div className={styles.uploads__upload__image}>
                            <img src={frontFile.url} alt={`${u.name} Front`} />
                          </div>
                        )}
                        {!frontFile && (
                          <div className={styles.uploads__upload__content}>
                            <p>Drag your file here, or click to Upload.</p>
                          </div>
                        )}
                        <footer>{u.name} - Front</footer>
                      </div>
                    )}
                    subFolder={`bondsman/${agency.bondsmanId}/employee/${token}/uploads`}
                    fetchSignedUrl={_fetchSignedUrl}
                    onUpload={_uploadCompleted}
                    styles={{ width: '100%', height: 'auto' }}
                  />
                  <Uploader
                    id={`back_${u.id}`}
                    key={`back_${u.id}`}
                    content={(
                      <div className={styles.uploads__upload}>
                        {backFile && (
                          <div className={styles.uploads__upload__image}>
                            <img src={backFile.url} alt={`${u.name} Back`} />
                          </div>
                        )}
                        {!backFile && (
                          <div className={styles.uploads__upload__content}>
                            <p>Drag your file here, or click to Upload.</p>
                          </div>
                        )}
                        <footer>{u.name} - Back</footer>
                      </div>
                    )}
                    subFolder={`bondsman/${agency.bondsmanId}/employee/${token}/uploads`}
                    fetchSignedUrl={_fetchSignedUrl}
                    onUpload={_uploadCompleted}
                    styles={{ width: '100%', height: 'auto' }}
                  />
                </div>
              )}
            </div>
          )
        }
        return (
          <Uploader
            id={`front_${u.id}`}
            key={`front_${u.id}`}
            content={(
              <div className={styles.uploads__upload}>
                <div className={styles.uploads__upload__content}>
                  <p>Drag your file here, or click to Upload.</p>
                </div>
                <footer>{u.name}</footer>
              </div>
            )}
            subFolder={`bondsman/${agency.bondsmanId}/employee/${token}/uploads`}
            fetchSignedUrl={_fetchSignedUrl}
            onUpload={_uploadCompleted}
            styles={{ width: '100%', height: 'auto' }}
          />
        )
      })
  }

  const _getCompleteButton = () => {
    if (displayStates) {
      let isComplete = true
      Object.keys(displayStates).forEach(id => {
        if (!displayStates[id].isCompleted) isComplete = false
      })
      return (
        <footer className={styles.uploads__footer}>
          <div>
            <ButtonPrimary color="grey" icon={faAngleDoubleLeft} onClick={() => navigate('/onboarding')}>Back</ButtonPrimary>
          </div>
          <div>
            <ButtonPrimary isIconRightSide color="navy" icon={faAngleDoubleRight} onClick={() => navigate('/onboarding')} disabled={!isComplete}>Continue</ButtonPrimary>
          </div>
        </footer>
      )
    }
    return null
  }

  return (
    <div className={styles.uploads}>
      <header className={styles.primaryHeader}>
        <h2>Uploads</h2>
        <p>Below are a list of uploads that are required for the </p>
      </header>
      {(files && surety && requirements) && _getRequiredUploads()}
      {_getCompleteButton()}
    </div>
  )
}

export default OnBoardingUploads
